import { FC } from "react";
import { TextField, InputLabel } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { Controller } from "react-hook-form";

import { AddIcon, Trash } from "@bms/assets";
import useWorkCost, { Props, ReceivedProps } from "./hook";
import { Styles } from "./styled";
import { ModalDelete } from "@bms/components";

const WorkCostLayout: FC<Props> = ({
  register,
  setValue,
  getValues,
  append,
  handleValue,
  remove,
  setOpenModalDelete,
  trigger,
  openModalDelete,
  errors,
  fields,
  isView,
  control,
  removeIdx,
  setRemoveIdx,
  formatNumber,
  formatQuantity,
  handleMoneyFieldChange,
  handleQuantityFieldChange,
  handleTextFieldChange,
  enforceTextMaxLength,
  handleInputWithComposition,
  handleCompositionEnd,
  handleControlledTextInput,
  getControlledValue,
  handleKeyPressBlock,
  handleStrictInput,
  handleNumberBlur,
  handleQuantityBlur,
  handleCompositionStart,
  handleCompositionEndFixed,
}) => {
  return (
    <Styles>
      <div className="main">
        <div className="field-wrapper">
          <div className="input-field">
            <InputLabel>出張料</InputLabel>
            <Controller
              name="busines_trip_fee"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={formatNumber(field.value) || ""}
                  onChange={(e) => {
                    handleMoneyFieldChange(
                      e.target.value,
                      "busines_trip_fee",
                      8,
                      99999999,
                      true
                    );
                  }}
                  placeholder="出張料を入力"
                  disabled={isView}
                  onKeyDown={(e) => {
                    if (e.key === "-") e.preventDefault();
                  }}
                  onBlur={() => {
                    handleNumberBlur("busines_trip_fee", true);
                  }}
                />
              )}
            />

            {errors?.busines_trip_fee && (
              <p className="error">{errors.busines_trip_fee?.message}</p>
            )}
          </div>

          <div className="input-field">
            <InputLabel>作業代</InputLabel>
            <Controller
              name="work_fee"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={formatNumber(field.value) || ""}
                  onChange={(e) => {
                    handleMoneyFieldChange(
                      e.target.value,
                      "work_fee",
                      8,
                      99999999
                    );
                  }}
                  placeholder="作業代を入力"
                  disabled={isView}
                  onKeyDown={(e) => {
                    if (e.key === "-") e.preventDefault();
                  }}
                  onBlur={() => {
                    handleNumberBlur("work_fee");
                  }}
                />
              )}
            />

            {errors?.work_fee && (
              <p className="error">{errors.work_fee?.message}</p>
            )}
          </div>

          <div className="input-field">
            <InputLabel>処分代</InputLabel>
            <Controller
              name="disposal_fee"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={formatNumber(field.value) || ""}
                  onChange={(e) => {
                    handleMoneyFieldChange(
                      e.target.value,
                      "disposal_fee",
                      8,
                      99999999
                    );
                  }}
                  placeholder="処分代を入力"
                  disabled={isView}
                  onKeyDown={(e) => {
                    if (e.key === "-") e.preventDefault();
                  }}
                  onBlur={() => {
                    handleNumberBlur("disposal_fee");
                  }}
                />
              )}
            />

            {errors?.disposal_fee && (
              <p className="error">{errors.disposal_fee?.message}</p>
            )}
          </div>

          <div className="input-field">
            <InputLabel>部材代(合計)</InputLabel>
            <Controller
              control={control}
              name="total_material_cost"
              render={({ field: { value } }) => (
                <NumericFormat
                  style={{ width: 240 }}
                  thousandSeparator=","
                  decimalSeparator="."
                  customInput={TextField}
                  value={value || ""}
                  {...register("total_material_cost")}
                  placeholder="自動で合計金額算出されます"
                  disabled={true}
                  InputLabelProps={{
                    shrink: Boolean(getValues("total_material_cost")),
                  }}
                />
              )}
            />
          </div>
        </div>

        <div className="field-wrapper">
          <div className="input-field">
            <Controller
              name="total"
              control={control}
              render={({ field: { value } }) => (
                <NumericFormat
                  thousandSeparator=","
                  decimalSeparator="."
                  customInput={TextField}
                  {...register("total")}
                  value={value || ""}
                  variant="standard"
                  label="合計"
                  disabled={true}
                  InputLabelProps={{
                    shrink:
                      getValues("total") !== 0 || getValues("total") === 0,
                  }}
                />
              )}
            />
          </div>
        </div>

        {fields.map((item, index) => (
          <div
            className="field-wrapper additional-field"
            key={item.id}
            style={{ alignItems: "center" }}
          >
            <div className="input-field">
              <InputLabel>部材代</InputLabel>
              <Controller
                name={`work_cost_items.${index}.material_cost`}
                control={control}
                render={({ field: { value } }) => (
                  <TextField
                    value={formatNumber(value) || ""}
                    onChange={(e) => {
                      handleMoneyFieldChange(
                        e.target.value,
                        `work_cost_items.${index}.material_cost`,
                        8,
                        99999999
                      );
                    }}
                    placeholder="部材代を入力"
                    disabled={isView}
                    onKeyDown={(e) => {
                      if (e.key === "-") e.preventDefault();
                    }}
                    onBlur={() => {
                      handleNumberBlur(
                        `work_cost_items.${index}.material_cost`
                      );
                    }}
                  />
                )}
              />
            </div>
            <div className="input-field">
              <InputLabel>品名</InputLabel>
              <TextField
                name={`work_cost_items.${index}.product_name`}
                placeholder="品名を入力"
                disabled={isView}
                inputProps={{ maxLength: 100 }}
                value={getControlledValue(
                  `work_cost_items.${index}.product_name`
                )}
                onChange={(e) => {
                  handleStrictInput(
                    e,
                    `work_cost_items.${index}.product_name`,
                    100
                  );
                }}
                onInput={(e) => {
                  handleStrictInput(
                    e,
                    `work_cost_items.${index}.product_name`,
                    100
                  );
                }}
                onKeyPress={(e) => {
                  handleKeyPressBlock(
                    e,
                    `work_cost_items.${index}.product_name`,
                    100
                  );
                }}
                onCompositionStart={() => {
                  handleCompositionStart(
                    `work_cost_items.${index}.product_name`
                  );
                }}
                onCompositionEnd={(e) => {
                  handleCompositionEndFixed(
                    e,
                    `work_cost_items.${index}.product_name`,
                    100
                  );
                }}
                onBlur={(e) => {
                  handleStrictInput(
                    e,
                    `work_cost_items.${index}.product_name`,
                    100
                  );
                }}
              />
            </div>
            <div className="input-field">
              <InputLabel>品番</InputLabel>
              <TextField
                {...register(`work_cost_items.${index}.part_number`)}
                name={`work_cost_items.${index}.part_number`}
                placeholder="品番を入力"
                disabled={isView}
                onChange={(e) => {
                  handleTextFieldChange(
                    e.target.value,
                    `work_cost_items.${index}.part_number`
                  );
                }}
              />
            </div>
            <div className="input-field">
              <InputLabel>数量</InputLabel>
              <Controller
                name={`work_cost_items.${index}.quantity`}
                control={control}
                render={(props: any) => (
                  <TextField
                    value={formatQuantity(props?.field?.value) || ""}
                    onChange={(e) => {
                      handleQuantityFieldChange(
                        e.target.value,
                        `work_cost_items.${index}.quantity`,
                        4,
                        9999
                      );
                    }}
                    placeholder="数量を入力"
                    disabled={isView}
                    onKeyDown={(e) => {
                      if (e.key === "-") e.preventDefault();
                    }}
                    onBlur={() => {
                      handleQuantityBlur(`work_cost_items.${index}.quantity`);
                    }}
                  />
                )}
              />
            </div>

            <div
              className="input-field"
              style={{
                marginRight: isView ? "48px" : 0,
              }}
            >
              <InputLabel>単位</InputLabel>
              <TextField
                name={`work_cost_items.${index}.unit`}
                placeholder="単位名を入力"
                disabled={isView}
                inputProps={{ maxLength: 5 }}
                value={getControlledValue(`work_cost_items.${index}.unit`)}
                onChange={(e) => {
                  handleStrictInput(e, `work_cost_items.${index}.unit`, 5);
                }}
                onInput={(e) => {
                  handleStrictInput(e, `work_cost_items.${index}.unit`, 5);
                }}
                onKeyPress={(e) => {
                  handleKeyPressBlock(e, `work_cost_items.${index}.unit`, 5);
                }}
                onCompositionStart={() => {
                  handleCompositionStart(`work_cost_items.${index}.unit`);
                }}
                onCompositionEnd={(e) => {
                  handleCompositionEndFixed(
                    e,
                    `work_cost_items.${index}.unit`,
                    5
                  );
                }}
                onBlur={(e) => {
                  handleStrictInput(e, `work_cost_items.${index}.unit`, 5);
                }}
              />
            </div>
            {!isView && (
              <>
                {
                  <div
                    className="btn-delete"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingTop: 27,
                    }}
                  >
                    <img
                      src={Trash}
                      onClick={() => {
                        setRemoveIdx(index);
                        setOpenModalDelete(true);
                      }}
                    />
                  </div>
                }
              </>
            )}
          </div>
        ))}

        <ModalDelete
          isOpen={openModalDelete}
          isSubmitting={false}
          onDeleteRecord={async () => {
            if (removeIdx !== null && removeIdx !== undefined) {
              remove(removeIdx);
            }
            setOpenModalDelete(false);
            setRemoveIdx(null);
            handleValue();
          }}
          onClose={() => {
            setOpenModalDelete(false);
            setRemoveIdx(null);
          }}
        />

        {!isView && (
          <button
            disabled={isView}
            onClick={(e) => {
              e.preventDefault();
              append({
                material_cost: null,
                product_name: null,
                part_number: null,
                quantity: null,
                unit: null,
              });
              return false;
            }}
            onKeyPress={(e) => {
              e.preventDefault();
              return false;
            }}
            onKeyDown={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <img src={AddIcon} />
            <p>項目追加</p>
          </button>
        )}
      </div>
    </Styles>
  );
};

const WorkCost: FC<ReceivedProps> = (props) => (
  <WorkCostLayout {...useWorkCost(props)} />
);

export default WorkCost;
